import { Collapser } from "@msuite/ui/collapser";
import {
	Droppable,
	type DroppablePayloadBase,
	getDraggableType,
	getDroppablePayload,
} from "@msuite/ui/dnd";
import { useDisclosure } from "@msuite/ui/hooks";
import { cn } from "@msuite/ui/utils";
import { generateId, guardString } from "@msuite/utils/strings";
import type { Tables, Vehicle } from "@msuite/utils/types";
import { mutators, queries } from "@msuite/zero";
import { useZero, useZeroQuery } from "@msuite/zero/react";
import { syncHandler } from "@/api/sync-handler";
import { useAuth } from "@/hooks/use-auth";
import { useDailyContext } from "../daily/context";
import { CrewContext } from "./context";
import { CrewHeader } from "./crew-header";
import { CrewStaff } from "./crew-staff";
import { CrewTickets } from "./crew-tickets";

/** Props Interface */
interface CrewProps {
	crewId: string;
}

export function Crew({ crewId }: CrewProps) {
	/** Context */
	const { plan } = useDailyContext();

	/** Hooks */
	const disclosure = useDisclosure({ defaultIsOpen: true });
	const { isInteractionBlocked, showTicketsDisclosure } = useDailyContext();
	const z = useZero();
	const [crew] = useZeroQuery(queries.crews.byId({ crewId }));
	const [organization] = useZeroQuery(
		queries.organizations.byId({ orgId: guardString(crew?.orgId) }),
	);
	const { organization: callerOrganization, citadel } = useAuth();

	/** Computed */
	// Staffing belongs to the organisation that owns the crew, so the host of a board cannot fill a
	// partner's column and the partner can fill it on a board it does not own — a guest board,
	// where every other control stays blocked, included. The lock therefore derives from crew
	// ownership rather than from `isInteractionBlocked`, which a guest board pins to true.
	// `assertCrewOwnedByCaller` and the `userAssignments` write policies enforce that same
	// ownership rule; the two conditions beside it are the interface's own.
	//
	// The rule is asked of the caller's own organisation rather than of the board's, and that is
	// the difference from `canArrangeBoard` in the daily context: a partner staffing its column on
	// a host's board holds `update Plan` in its own company, which is the right this asks about.
	const canStaff = citadel?.can("update", "Plan", { orgId: citadel.orgId }) === true;
	// Publication freezes staffing on our own board, where the day can be pulled back by whoever
	// handed it out. It does not freeze a host's board: that state belongs to the host, a partner
	// cannot pull the day back, and a partner normally fills its column after the host has already
	// published — so reading the state of a board we do not own would lock us out of the one thing
	// a guest board is ours to change.
	const isOwnBoard = plan?.orgId === callerOrganization?.id;
	const isStaffingBlocked =
		(isOwnBoard && plan?.state === "published") ||
		crew?.orgId !== callerOrganization?.id ||
		!canStaff;

	/** Functions */
	async function handleOnDrop(element: DroppablePayloadBase) {
		const type = getDraggableType(element);
		if (type === "vehicle") {
			const item = getDroppablePayload<Vehicle>(element);
			await handleOnAssignVehicle(item.item.id);
		} else if (type === "vehicle-assignment") {
			const item = getDroppablePayload<Tables<"vehicleAssignments">>(element);
			await handleOnAssignVehicle(item.item.vehicleId);
		} else if (type === "vehicle-assignment-plan") {
			const item = getDroppablePayload<Tables<"vehicleAssignmentsPlan">>(element);
			await handleOnAssignVehicle(item.item.vehicleId);
		}
	}

	async function handleOnAssignVehicle(vehicleId: string) {
		if (!crew) return;
		syncHandler(
			z.mutate(mutators.vehicleAssignments.insert({ id: generateId(), crewId, vehicleId })),
		);
	}

	/** Guard */
	if (!crew) return null;
	if (!organization) return null;

	/** Render */
	return (
		<CrewContext.Provider value={{ crew, crewId: crew.id, planId: crew.planId, isStaffingBlocked }}>
			<Droppable
				accept={["vehicle", "vehicle-assignment-plan", "vehicle-assignment"]}
				className={cn("flex flex-col gap-2 shrink-0 w-full transition-all", "crew", "relative")}
				id={`crew-${crew.id}`}
				data-crew-id={crew.id}
				data-crew-name={crew.orgId}
				data-crew-organization-name={organization?.name}
				data-crew-ranking={crew.ranking}
				data-crew-collapsed={!disclosure.isOpen}
				handleOnDrop={handleOnDrop}
				isDisabled={isInteractionBlocked}
				canDrop={(payload) => {
					const item = getDroppablePayload(payload);
					if (!item.type.startsWith("ticket")) {
						if (plan?.orgId !== crew.orgId) return false;
					}
					return true;
				}}
			>
				<CrewHeader controller={disclosure} />
				<Collapser
					label={undefined}
					controller={disclosure}
					gap="small"
					withDivider={false}
				>
					<div className="gap-4 grid grid-cols-3 relative">
						<div className="v-stack gap-0">
							<CrewStaff />
						</div>
						{showTicketsDisclosure.isOpen && <CrewTickets />}
					</div>
				</Collapser>
			</Droppable>
		</CrewContext.Provider>
	);
}
